import { get } from "lodash";
import { useEffect, useState } from "react";
import { NAVIGATION_NOTE } from "../constants";
import { FileText, Info, LoaderCircle } from "lucide-react";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import {
  getLoadingMessage,
  isLoadingMessageAnnounced,
  toLoadingSchedule,
} from "../lib/loadingSchedule";

const CarrierLoader = ({
  loginInformation,
  loadingHeader,
  loadingSteps,
  loadingSchedule,
  processDocument = false,
  footerText = "",
  showFooter = true,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const loadingImage = get(
    loginInformation,
    "carrier.image",
    "https://axle-labs-assets.s3.amazonaws.com/carrierLogos/generic.svg"
  );

  const carrierName = get(loginInformation, "carrier.name", null);

  useEffect(() => {
    const startedAt = Date.now();
    const intervalId = setInterval(
      () => setElapsedSeconds((Date.now() - startedAt) / 1000),
      250
    );
    return () => clearInterval(intervalId);
  }, []);

  const schedule = loadingSchedule ?? toLoadingSchedule(loadingSteps);
  const message = getLoadingMessage(schedule, elapsedSeconds).replace(/\.+$/, "");

  return (
    <>
      <StepBody centered className="items-center gap-4">
        <div aria-hidden className="mb-4 flex w-full max-w-60 items-center">
          {processDocument ? (
            <span className="flex size-16 shrink-0 items-center justify-center rounded-full border border-subtle bg-surface-muted text-ink">
              <FileText aria-hidden className="size-6" strokeWidth={1.75} />
            </span>
          ) : (
            <span className="size-16 shrink-0 rounded-full bg-ink bg-logo-svg bg-5/8 bg-center bg-no-repeat" />
          )}
          <span className="relative mx-2 h-6 flex-1">
            <span className="absolute inset-x-0 top-1/2 border-t-2 border-dotted border-border" />
            {PACKET_DELAYS.map((delay) => (
              <span
                key={delay}
                style={{ animationDelay: delay }}
                className="absolute top-1/2 hidden size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-live-start to-live-end motion-safe:block motion-safe:animate-packet"
              />
            ))}
          </span>
          {processDocument ? (
            <span className="size-16 shrink-0 rounded-full bg-ink bg-logo-svg bg-5/8 bg-center bg-no-repeat" />
          ) : (
            <span className="relative flex shrink-0">
              <span className="absolute inset-0 rounded-full bg-gradient-to-br from-live-start to-live-end opacity-0 motion-safe:animate-beacon" />
              <span
                style={{ backgroundImage: `url("${loadingImage}")` }}
                className="relative size-16 rounded-full border border-subtle bg-surface bg-cover bg-center bg-no-repeat"
              />
            </span>
          )}
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          <StepHeading align="center" title={loadingHeader} />
          <div
            role="status"
            aria-live={
              isLoadingMessageAnnounced(schedule, elapsedSeconds) ? "polite" : "off"
            }
            className="flex items-center gap-2.5"
          >
            <LoaderCircle
              aria-hidden
              className="size-4 shrink-0 text-ink-secondary motion-safe:animate-spin"
              strokeWidth={2}
            />
            <span
              key={message}
              className="text-body-medium text-ink motion-safe:animate-fade-in"
            >
              {`${message}...`}
            </span>
          </div>
        </div>
      </StepBody>
      {showFooter && (
        <StepFooter>
          {footerText && (
            <p className="text-center text-body-lg text-ink-secondary">
              {footerText}
            </p>
          )}
          <FooterNote>
            {processDocument
              ? NAVIGATION_NOTE
              : `You may be notified by ${carrierName} of a new login attempt.`}
          </FooterNote>
        </StepFooter>
      )}
    </>
  );
};

const PACKET_DELAYS = ["0s", "0.6s", "1.2s"];

const FooterNote = ({ children }) => (
  <p className="flex items-start justify-center gap-2 text-body-lg text-ink-secondary">
    <Info aria-hidden className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
    <span>{children}</span>
  </p>
);

export default CarrierLoader;
